/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import { Modal, ModalBody, ModalClose, ModalContent, ModalFooter, ModalHeader, ModalTitle, ModalTrigger } from "../ui/Modal";
import Button from "../ui/Button";
import ButtonGroup from "../ui/ButtonGroup";
import usePluginUtils from "../../hooks/usePluginUtils";
import Text from "../ui/Text";
import { useForm } from "react-hook-form";
import FormGroup from "../ui/form/FormGroup";
import TextField from "../ui/form/TextField";
import { useDeleteBooking } from "../../lib/react-query/useDeleteBooking";
import Form from "../ui/form/Form";
import FormError from "../ui/form/FormError";
import { useStepManager } from "../../hooks/useStepManager";
import { BATCH_CANCEL_MODES, BOOKING_BATCH_TYPES, ROUTES } from "../../constants/constants";
import Checkbox from "../ui/form/Checkbox";
import { useConfig } from "../../lib/react-query/useConfig";
import { useBooking } from "../../hooks/useBooking";
import { extractErrorMessage } from "../../utils/api/apiErrors";

const CancelationModal = ({ booking, show = false, trigger = null }) => {
  const [isModalOpen, setIsModalOpen] = useState(show);
  const [error, setError] = useState(null);
  const { resetBookingRequest } = useBooking();
  const { data: config } = useConfig();
  const { isPluginEnabled } = usePluginUtils();
  const { navigateToStep } = useStepManager();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm();
  const { mutate, isPending } = useDeleteBooking(booking.id, !!booking.batch_type);

  const onSubmit = (data) => {
    mutate(data, {
      onSuccess: () => {
        setIsModalOpen(false);
        resetBookingRequest();
        reset();
        navigateToStep(ROUTES.BOOKING_CANCELLED);
      },
      onError: (error) => {
        setError(extractErrorMessage(error, "Something went wrong"));
        reset();
      },
    });
  };

  const handleBatchCancelCheckboxChange = (e) => {
    const modeValue =
      booking.batch_type === BOOKING_BATCH_TYPES.RECURRING
        ? BATCH_CANCEL_MODES.RECURRING_BATCH
        : booking.batch_type === BOOKING_BATCH_TYPES.MULTIPLE || booking.batch_type === BOOKING_BATCH_TYPES.GROUP
          ? BATCH_CANCEL_MODES.MULTIPLE_BATCH
          : undefined;

    if (e.target.checked) {
      setValue("mode", modeValue);
    } else {
      setValue("mode", undefined);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    error && setError(null);
    reset();
  };

  return (
    <Modal isOpen={isModalOpen} onOpen={() => setIsModalOpen(true)} onClose={handleModalClose}>
      {trigger && (
        <ModalTrigger>
          {trigger}
        </ModalTrigger>
      )}
      <ModalContent>
        <ModalHeader>
          <ModalTitle>cancellation_dialog_title</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {error && <Text color="dark">{error}</Text>}

          {!error && !isPluginEnabled("cancelation_policy") && (
            <Text color="dark">After cancelation it won't be possible to restore this booking</Text>
          )}

          <Form id="cancelationForm">
            {booking.batch_type === BOOKING_BATCH_TYPES.RECURRING && (
              <FormGroup>
                <Checkbox
                  control={control}
                  name="mode"
                  label="Delete all recurring batch appointments"
                  onChange={handleBatchCancelCheckboxChange}
                />
              </FormGroup>
            )}

            {(booking.batch_type === BOOKING_BATCH_TYPES.MULTIPLE || booking.batch_type === BOOKING_BATCH_TYPES.GROUP) && (
              <FormGroup>
                <Checkbox
                  control={control}
                  name="mode"
                  label="Delete all batch appointments"
                  onChange={handleBatchCancelCheckboxChange}
                />
              </FormGroup>
            )}

            {!error && isPluginEnabled("cancelation_policy") && config.cancelation_policy_enable_reason === "1" && (
              <>
                <Text color="dark">cancellation_dialog_body</Text>
                <FormGroup>
                  <TextField
                    control={control}
                    name="reason"
                    label="Reason"
                    placeholder="Reason"
                    required={config.cancelation_policy_is_reason_required}
                  />
                  {errors.reason && <FormError name="reason">{errors.reason.message}</FormError>}
                </FormGroup>
              </>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          {!error && (
            <ButtonGroup itemsWidth="half">
              <ModalClose>
                <Button variant="outline" title="Close" />
              </ModalClose>
              <Button
                form="cancelationForm"
                variant="primary"
                title="cancel booking"
                onClick={handleSubmit(onSubmit)}
                loading={isPending}
              />
            </ButtonGroup>
          )}

          {error && (
            <ButtonGroup itemsWidth="half" justify="end">
              <ModalClose>
                <Button variant="primary" title="Okay" onClick={handleModalClose} />
              </ModalClose>
            </ButtonGroup>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CancelationModal;
